import type { Entity as CurriculumEntity } from '@mathflat/domain/@entities/Curriculum/dto'
import { curriculumUtils } from '@mathflat/domain/@entities/Curriculum/util'
import type { MathflatApi } from '@mathflat/domain/@entities/Lecture/api'
import { Entity as LectureEntity } from '@mathflat/domain/@entities/Lecture/dto'
import { SCHOOL_TYPE } from '@mathflat/domain/@entities/SchoolSystem/constants.ts'
import type { SchoolType } from '@mathflat/domain/@entities/SchoolSystem/schema.ts'
import type { StudentDomain } from '@mathflat/domain/@entities/Student/domain.ts'
import type { StudentLectureDtoT } from '@mathflat/domain/@entities/StudentLecture'
import type { ZonedISODateTime } from '@mathflat/shared/@types/date'
import { groupBy } from 'lodash-es'
import { makeAutoObservable, runInAction } from 'mobx'

import { lectureApi } from '~/@common/api/lectureApi'
import { commonRepo } from '~/@common/services/repo.service.ts'

export const LECTURE_STATUS_FILTER = {
  ALL: 'ALL',
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  EXPIRED: 'EXPIRED',
} as const

export type LectureStatusFilter = (typeof LECTURE_STATUS_FILTER)[keyof typeof LECTURE_STATUS_FILTER]

export const LECTURE_STATUS_FILTER_LABEL: Record<LectureStatusFilter, string> = {
  [LECTURE_STATUS_FILTER.ALL]: '전체',
  [LECTURE_STATUS_FILTER.NOT_STARTED]: '학습가능',
  [LECTURE_STATUS_FILTER.IN_PROGRESS]: '학습중',
  [LECTURE_STATUS_FILTER.COMPLETED]: '학습완료',
  [LECTURE_STATUS_FILTER.EXPIRED]: '기간만료',
}

export type LectureGroup = {
  leadingCurriculum: MathflatApi.Response.LectureByTrieKey['curriculum'] // 대단원이 될수도 중단원이 될 수도
  middleChapterLectures: MiddleChapterLecture[]
}
export class LectureService {
  lectureGroups?: LectureGroup[]
  _selectedCurriculumInfo: CurriculumEntity.CurriculumCoreItem | null = null
  isFetching = false
  isShowAllLectures = false

  constructor() {
    makeAutoObservable(this)
  }

  get selectedCurriculumInfo() {
    if (this._selectedCurriculumInfo) return this._selectedCurriculumInfo
    if (!commonRepo.studentCurriculumInfo) return null
    // SOLO 는 구독 가능한 첫 curriculum, 그 외는 중1 디폴트
    const { bySubscription } = commonRepo.permission
    if (bySubscription.isSolo) {
      return (
        this.flatAndSelectableCurriculumList.find((c) =>
          bySubscription.isSubscribed({ trieKey: c.trieKey }),
        ) ?? this.flatAndSelectableCurriculumList[0]
      )
    }
    return commonRepo.curriculumCore?.getGradeSemesterMiddle({ includeAll: false })[0]
  }

  // 커리큘럼 선택
  setCurriculumAndFetchLectureGroups(
    {
      curriculumInfo,
      studentId,
    }: {
      curriculumInfo: CurriculumEntity.CurriculumCoreItem
      studentId: StudentDomain.Id
    },
    curriculumType: CurriculumEntity.Curriculum['type'] = 'BIG_CHAPTER',
  ) {
    if (studentId) {
      this._selectedCurriculumInfo = curriculumInfo
      this.fetchSetLectureGroups({ trieKey: curriculumInfo.trieKey, studentId }, curriculumType)
    }
  }

  // 초중고 선택
  setSelectSchoolType = (schoolType: SchoolType) => {
    if (!this.selectableCurriculumList[schoolType])
      throw Error('selectableDropdownList[schoolType] is nullable')

    if (!commonRepo.studentId) {
      throw Error('studentId is missing')
    }

    const curriculums = this.selectableCurriculumList[schoolType]

    if (!curriculums) throw Error('curriculums is nullable')

    // SOLO 면 해당 학교급 안에서 구독 가능한 첫 curriculum, 아니면 첫 항목
    const { bySubscription } = commonRepo.permission
    const target = bySubscription.isSolo
      ? curriculums.find((c) => bySubscription.isSubscribed({ trieKey: c.trieKey })) ??
        curriculums[0]
      : curriculums[0]

    this.setCurriculumAndFetchLectureGroups({
      curriculumInfo: target,
      studentId: commonRepo.studentId,
    })
  }

  get selectableCurriculumList() {
    return {
      [SCHOOL_TYPE.초등학교]: commonRepo.curriculumCore
        ?.getGradeSemesterElementary({ includeAll: false })
        .filter((c) => c.gradeSubject !== '1' && c.gradeSubject !== '2'),
      [SCHOOL_TYPE.중학교]: commonRepo.curriculumCore?.getGradeSemesterMiddle({
        includeAll: false,
        includePreOpen: false,
      }),
      [SCHOOL_TYPE.고등학교]: commonRepo.curriculumCore?.getGradeSubjectHigh({
        includeAll: false,
        includePreOpen: false,
      }),
    }
  }
  makeCurriculumKeyForGradeSemester = (trieKey: CurriculumEntity.TrieKey) => {
    const splittedStr = trieKey.split('.')

    if (splittedStr.length < 5) return trieKey
    if (splittedStr.length < 7) return splittedStr.slice(0, 4).join('.') // 고등학교 단원 커리큘럼키 대응
    return splittedStr.slice(0, 5).join('.')
  }

  get flatAndSelectableCurriculumList(): CurriculumEntity.CurriculumCoreItem[] {
    return Object.values(this.selectableCurriculumList)
      .flat()
      .filter((v): v is CurriculumEntity.CurriculumCoreItem => !!v)
  }

  fetchLectureGroups = async (trieKey: CurriculumEntity.TrieKey, studentId: StudentDomain.Id) => {
    return await Promise.all([
      lectureApi.getLectureByTrieKey(trieKey),
      lectureApi.getDoneLectureIdByTrieKey({ studentId, trieKey }),
    ])
      .then(([lectures, doneLectureIds]) => {
        const lectureGroups = lectures.map(({ leadingCurriculum, middleChapterLectures }) => ({
          leadingCurriculum,
          middleChapterLectures: middleChapterLectures.flatMap(({ curriculum, lectures }) =>
            lectures.map(
              (lecture) =>
                new MiddleChapterLecture({
                  ...lecture,
                  isDone: doneLectureIds.includes(lecture.id),
                  bigChapterName: leadingCurriculum.name,
                  middleChapterName: curriculum.name,
                  trieKey: lecture.curriculum.key,
                }),
            ),
          ),
        }))
        return lectureGroups
      })
      .catch((e) => {
        console.error(e)
        throw e
      })
      .finally(() => {
        this.updateIsFetching(false)
      })
  }

  fetchSetLectureGroups = async (
    {
      trieKey,
      studentId,
    }: {
      trieKey: CurriculumEntity.TrieKey
      studentId: StudentDomain.Id
    },
    breakdown: CurriculumEntity.Curriculum['type'] = 'BIG_CHAPTER',
  ) => {
    try {
      this.updateIsFetching(true)

      const lectureGroups = await this.fetchLectureGroups(trieKey, studentId)
      if (breakdown === 'BIG_CHAPTER') {
        runInAction(() => {
          this.lectureGroups = lectureGroups
        })
        return
      }
      // MIDDLE_CHAPTER
      const middleChapterLecturesList = lectureGroups.flatMap((lectureGroup) => {
        return Object.values(
          groupBy(lectureGroup.middleChapterLectures, 'trieKey'),
        ) as MiddleChapterLecture[][]
      })

      runInAction(() => {
        this.lectureGroups = middleChapterLecturesList.map((middleChapterLectures) => ({
          leadingCurriculum: {
            id: middleChapterLectures[0].id,
            name: middleChapterLectures[0].middleChapterName,
            type: 'MIDDLE_CHAPTER',
          },
          middleChapterLectures,
        }))
      })
    } catch (e) {
      console.error(e)
      throw e
    } finally {
      this.updateIsFetching(false)
    }
  }

  setLectureGroups = (lectureGroups: LectureGroup[]) => {
    this.lectureGroups = lectureGroups
  }

  toggleShowAllLectures = () => {
    this.isShowAllLectures = !this.isShowAllLectures
  }

  static findLectureGroupByLectureId = (
    lectureGroups: LectureGroup[],
    lectureId: MiddleChapterLecture['id'],
  ) => {
    return lectureGroups.find((lectureGroup) =>
      lectureGroup.middleChapterLectures.find((lecture) => lecture.id === lectureId),
    )
  }

  getLectureGroupById = async (
    lectureId: MiddleChapterLecture['id'],
    studentId: StudentDomain.Id,
  ) => {
    let lectureGroups = this.lectureGroups
    let lectureGroup = lectureGroups
      ? LectureService.findLectureGroupByLectureId(lectureGroups, lectureId)
      : undefined
    if (!lectureGroups || !lectureGroup) {
      const lecture = await lectureApi.getLectureByLectureId(lectureId)

      // 대단원 목록을 조회해야하는데, 대단원 조회할 커리큘럼 키를 몰라서, 중단원키에서 대단원키를 추출하는 과정을 거침
      // 제약조건: 지금이 중단원일 때만 가능하게 함
      if (lecture.curriculum.type !== 'MIDDLE_CHAPTER') {
        throw Error('중단원 조회가 아닙니다.')
      }

      const bigChapterCurriculumKey = curriculumUtils.extractUpperCurriculumKey(
        lecture.curriculum.key,
        lecture.curriculum.id,
      )

      lectureGroups = await this.fetchLectureGroups(bigChapterCurriculumKey, studentId)
      lectureGroup = LectureService.findLectureGroupByLectureId(lectureGroups, lectureId)
    }
    return lectureGroup
  }

  setProgress = async ({
    studentId,
    lecture,
    assignmentId,
  }: {
    studentId: StudentDomain.Id
    lecture: MiddleChapterLecture
    assignmentId?: StudentLectureDtoT.AssignmentResponse['assignmentId']
  }) => {
    await lectureApi.postStudentLecture({ studentId, lectureId: lecture.id, assignmentId })
  }
  setAssignmentStatusCompleted = async (
    assignmentId: StudentLectureDtoT.AssignmentResponse['assignmentId'],
  ) => {
    await lectureApi.postStudentLectureAssignmentStatus({ assignmentId })
  }

  updateIsFetching(fetchingStatus: boolean) {
    this.isFetching = fetchingStatus
  }
}

export class MiddleChapterLecture extends LectureEntity.Lecture {
  isDone: boolean
  bigChapterName: CurriculumEntity.Curriculum['name']
  middleChapterName: CurriculumEntity.Curriculum['name']
  trieKey: CurriculumEntity.Curriculum['key']

  constructor(
    params: LectureEntity.Lecture & {
      isDone: boolean
      bigChapterName: CurriculumEntity.Curriculum['name']
      middleChapterName: CurriculumEntity.Curriculum['name']
      trieKey: CurriculumEntity.Curriculum['key']
      latestPlayedAt: ZonedISODateTime | null
      videoLengthSeconds: number
    },
  ) {
    super(params)
    this.isDone = params.isDone
    this.bigChapterName = params.bigChapterName
    this.middleChapterName = params.middleChapterName
    this.trieKey = params.trieKey
    this.latestPlayedAt = params.latestPlayedAt
    this.videoLengthSeconds = params.videoLengthSeconds
  }
}
